import {
  CATEGORIES,
  type WineCategory,
  type WineFields,
} from "../types";

export const WINE_PHOTO_AI_FIELDS = [
  "producteur",
  "nom",
  "appellation",
  "region",
  "cepages",
  "vinification_elevage",
  "millesime",
  "categorie",
  "format",
] as const;

export type WinePhotoAiField = (typeof WINE_PHOTO_AI_FIELDS)[number];

export type WineAiField = WinePhotoAiField;

export type WinePhotoAiValues = Partial<
  Pick<WineFields, WinePhotoAiField>
>;

export type WineAiConfidence = "haute" | "moyenne" | "faible";
export type WineAiOrigin = "etiquette";

export interface WineAiSuggestion {
  field: WineAiField;
  value: string | number | null;
  confidence: WineAiConfidence;
  origin: WineAiOrigin;
  evidence: string;
}

export interface WineAiSource {
  title: string;
  url: string;
}

export interface WineAiAnalysisResult {
  mode: "rapide";
  model: string;
  suggestions: WineAiSuggestion[];
  warnings: string[];
  sources: WineAiSource[];
}

export interface WinePhotoAiParseResult {
  parsed: boolean;
  values: WinePhotoAiValues;
  warnings: string[];
}

export const WINE_PHOTO_AI_JSON_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["vin", "avertissements"],
  properties: {
    vin: {
      type: "object",
      additionalProperties: false,
      properties: {
        producteur: { type: "string", minLength: 1, maxLength: 200 },
        nom: { type: "string", minLength: 1, maxLength: 200 },
        appellation: { type: "string", minLength: 1, maxLength: 500 },
        region: { type: "string", minLength: 1, maxLength: 500 },
        cepages: { type: "string", minLength: 1, maxLength: 500 },
        vinification_elevage: {
          type: "string",
          minLength: 1,
          maxLength: 10_000,
        },
        millesime: {
          type: ["integer", "null"],
          minimum: 1000,
          maximum: 9999,
        },
        categorie: { type: "string", enum: [...CATEGORIES] },
        format: { type: "string", minLength: 1, maxLength: 80 },
      },
    },
    avertissements: {
      type: "array",
      maxItems: 20,
      items: { type: "string", minLength: 1, maxLength: 500 },
    },
  },
} as const;

const FIELD_SET = new Set<string>(WINE_PHOTO_AI_FIELDS);
const RESPONSE_WRAPPERS = ["vin", "wine", "valeurs", "values"] as const;
const RESPONSE_METADATA = new Set([
  ...RESPONSE_WRAPPERS,
  "avertissements",
  "warnings",
]);

const SINGLE_LINE_LIMITS = {
  producteur: 200,
  nom: 200,
  appellation: 500,
  region: 500,
  cepages: 500,
  format: 80,
} as const;

function normalizeKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLocaleLowerCase("fr-FR")
    .replace(/[^a-z0-9]+/gu, " ")
    .trim()
    .replace(/\s+/gu, " ");
}

const CATEGORY_ALIASES: Record<string, WineCategory> = {
  rouge: "Rouge",
  red: "Rouge",
  blanc: "Blanc",
  white: "Blanc",
  effervescent: "Effervescent",
  champagne: "Effervescent",
  mousseux: "Effervescent",
  sparkling: "Effervescent",
  "sparkling wine": "Effervescent",
  doux: "Liquoreux",
  liquoreux: "Liquoreux",
  "doux liquoreux": "Liquoreux",
  "doux liquoreux non fortifie": "Liquoreux",
  "sweet wine": "Liquoreux",
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function cleanSingleLine(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/gu, " ");
}

function cleanMultiline(value: string): string {
  return value
    .normalize("NFKC")
    .replace(/\r\n?|\u2028|\u2029/gu, "\n")
    .replace(/[\p{Zs}\t\f\v]+/gu, " ")
    .replace(/ *\n */gu, "\n")
    .replace(/\n{3,}/gu, "\n\n")
    .trim();
}

function decimalLabel(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return String(rounded).replace(".", ",");
}

function normalizeFormat(value: string): string | null {
  const cleaned = cleanSingleLine(value);
  const match = /^(\d+(?:[.,]\d+)?)\s*(ml|cl|l|litre|litres)$/iu.exec(cleaned);
  if (!match) return cleaned;

  const amount = Number(match[1].replace(",", "."));
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const unit = match[2].toLocaleLowerCase("fr-FR");
  const millilitres = unit === "ml" ? amount : unit === "cl" ? amount * 10 : amount * 1000;
  if (millilitres >= 1000) return `${decimalLabel(millilitres / 1000)} L`;
  return `${decimalLabel(millilitres / 10)} cl`;
}

function uniqueWarnings(warnings: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const warning of warnings) {
    const cleaned = cleanSingleLine(warning).slice(0, 500);
    if (!cleaned || seen.has(cleaned)) continue;
    seen.add(cleaned);
    result.push(cleaned);
    if (result.length === 20) break;
  }
  return result;
}

function jsonCandidates(source: string): string[] {
  const candidates = [source.replace(/^\uFEFF/u, "").trim()];
  const fences = /```(?:json)?\s*([\s\S]*?)```/giu;
  for (const match of source.matchAll(fences)) {
    const candidate = match[1].replace(/^\uFEFF/u, "").trim();
    if (candidate) candidates.push(candidate);
  }
  return [...new Set(candidates)].filter(Boolean);
}

function parseJsonObject(source: string): Record<string, unknown> | null {
  for (const candidate of jsonCandidates(source)) {
    try {
      const parsed: unknown = JSON.parse(candidate);
      if (isRecord(parsed)) return parsed;
    } catch {
      // Une réponse collée peut contenir du texte autour d'un bloc JSON valide.
    }
  }
  return null;
}

function responseValues(
  root: Record<string, unknown>,
  warnings: string[],
): Record<string, unknown> {
  for (const key of RESPONSE_WRAPPERS) {
    if (!Object.prototype.hasOwnProperty.call(root, key)) continue;
    if (isRecord(root[key])) return root[key];
    warnings.push(`Le champ « ${key} » doit contenir un objet JSON.`);
    return {};
  }
  return root;
}

function collectModelWarnings(root: Record<string, unknown>): string[] {
  const result: string[] = [];
  for (const key of ["avertissements", "warnings"] as const) {
    const value = root[key];
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === "string") result.push(item);
    }
  }
  return result;
}

export function buildWinePhotoAnalysisPrompt(photoCount: 1 | 2): string {
  if (photoCount !== 1 && photoCount !== 2) {
    throw new RangeError("Le nombre de photos doit être 1 ou 2.");
  }

  const photoInstruction =
    photoCount === 1
      ? "Une photo de la bouteille est jointe. Analyse uniquement ce qui y est réellement lisible."
      : "Deux photos de la même bouteille sont jointes, généralement l’étiquette avant et la contre-étiquette. Croise-les sans supposer leur ordre.";

  return [
    "Tu extrais des informations factuelles d’étiquettes de vin pour préremplir une fiche.",
    photoInstruction,
    "",
    "Règles impératives :",
    "- N’utilise ni recherche web, ni mémoire générale sur le vin, ni déduction commerciale. N’invente rien.",
    "- Recopie les noms propres avec leurs accents. Ne traduis pas l’étiquette.",
    "- Distingue le producteur ou domaine du négociant, de l’importateur et du simple embouteilleur. En cas d’ambiguïté, omets `producteur` et explique-la dans `avertissements`.",
    "- Pour `nom`, utilise la cuvée ou le nom distinctif affiché. S’il n’existe aucune cuvée distincte, l’appellation affichée peut servir de nom, avec un avertissement.",
    "- N’ajoute `millesime` comme nombre que si l’année est clairement le millésime, jamais une année de fondation ou de classement.",
    "- Si l’étiquette indique explicitement NM, NV ou non millésimé, renvoie `millesime: null`. Si le millésime est seulement absent ou illisible, omets entièrement la clé `millesime`.",
    `- Pour \`categorie\`, utilise exactement une de ces valeurs : ${CATEGORIES.map((item) => `« ${item} »`).join(", ")}. Omets-la si la catégorie n’est pas certaine.`,
    "- Pour `cepages`, recopie uniquement les cépages explicitement lisibles sur l’étiquette. Ne les déduis jamais de l’appellation, de la région ou de ta connaissance du vin. Conserve les assemblages et pourcentages sous forme de texte, dans l’ordre affiché.",
    "- Pour `vinification_elevage`, ne reprends que les méthodes ou durées explicitement écrites sur l’étiquette.",
    "- N’essaie jamais de déterminer quantité, emplacement, prix, vendeur, note personnelle ou période de dégustation depuis la bouteille.",
    "- Toute information incertaine, contradictoire ou illisible doit être omise de `vin` et expliquée brièvement dans `avertissements`.",
    "",
    "Réponds par un unique objet JSON, sans Markdown, sans bloc de code et sans texte avant ou après.",
    "L’objet `vin` est un Partial<WineFields> : il ne contient que les champs effectivement exploitables.",
    "Respecte exactement ce JSON Schema :",
    JSON.stringify(WINE_PHOTO_AI_JSON_SCHEMA, null, 2),
  ].join("\n");
}

export function parseWinePhotoAnalysisResponse(
  response: string,
): WinePhotoAiParseResult {
  if (typeof response !== "string" || !response.trim()) {
    return {
      parsed: false,
      values: {},
      warnings: ["La réponse de l’IA est vide."],
    };
  }

  const root = parseJsonObject(response);
  if (!root) {
    return {
      parsed: false,
      values: {},
      warnings: ["La réponse de l’IA ne contient pas d’objet JSON valide."],
    };
  }

  const warnings = collectModelWarnings(root);
  const rawValues = responseValues(root, warnings);
  const values: WinePhotoAiValues = {};

  for (const key of Object.keys(rawValues)) {
    if (!FIELD_SET.has(key) && !RESPONSE_METADATA.has(key)) {
      warnings.push(`Le champ non autorisé « ${key} » a été ignoré.`);
    }
  }

  for (const field of [
    "producteur",
    "nom",
    "appellation",
    "region",
    "cepages",
  ] as const) {
    const raw = rawValues[field];
    if (raw === undefined || raw === null) continue;
    if (typeof raw !== "string") {
      warnings.push(`Le champ « ${field} » doit être du texte et a été ignoré.`);
      continue;
    }
    const cleaned = cleanSingleLine(raw);
    if (!cleaned) {
      warnings.push(`Le champ vide « ${field} » a été ignoré.`);
      continue;
    }
    if (cleaned.length > SINGLE_LINE_LIMITS[field]) {
      warnings.push(`Le champ « ${field} » est trop long et a été ignoré.`);
      continue;
    }
    values[field] = cleaned;
  }

  const rawVinification = rawValues.vinification_elevage;
  if (rawVinification !== undefined && rawVinification !== null) {
    if (typeof rawVinification !== "string") {
      warnings.push(
        "Le champ « vinification_elevage » doit être du texte et a été ignoré.",
      );
    } else {
      const cleaned = cleanMultiline(rawVinification);
      if (!cleaned) {
        warnings.push("Le champ vide « vinification_elevage » a été ignoré.");
      } else if (cleaned.length > 10_000) {
        warnings.push("Le champ « vinification_elevage » est trop long et a été ignoré.");
      } else {
        values.vinification_elevage = cleaned;
      }
    }
  }

  if (Object.prototype.hasOwnProperty.call(rawValues, "millesime")) {
    const raw = rawValues.millesime;
    if (raw === null) {
      values.millesime = null;
    } else if (
      typeof raw === "string" &&
      ["nm", "nv", "non millesime", "sans millesime"].includes(normalizeKey(raw))
    ) {
      values.millesime = null;
    } else {
      const year =
        typeof raw === "number"
          ? raw
          : typeof raw === "string" && /^\d{4}$/u.test(raw.trim())
            ? Number(raw.trim())
            : Number.NaN;
      if (Number.isInteger(year) && year >= 1000 && year <= 9999) {
        values.millesime = year;
      } else {
        warnings.push("Le millésime n’est pas une année valide et a été ignoré.");
      }
    }
  }

  const rawCategory = rawValues.categorie;
  if (rawCategory !== undefined && rawCategory !== null) {
    if (typeof rawCategory !== "string") {
      warnings.push("La catégorie doit être du texte et a été ignorée.");
    } else {
      const category = CATEGORY_ALIASES[normalizeKey(rawCategory)];
      if (category) values.categorie = category;
      else warnings.push(`La catégorie « ${cleanSingleLine(rawCategory)} » n’est pas reconnue.`);
    }
  }

  const rawFormat = rawValues.format;
  if (rawFormat !== undefined && rawFormat !== null) {
    if (typeof rawFormat !== "string") {
      warnings.push("Le format doit être du texte et a été ignoré.");
    } else {
      const format = normalizeFormat(rawFormat);
      if (!format) {
        warnings.push("Le format n’est pas valide et a été ignoré.");
      } else if (format.length > SINGLE_LINE_LIMITS.format) {
        warnings.push("Le format est trop long et a été ignoré.");
      } else {
        values.format = format;
      }
    }
  }

  if (!Object.keys(values).length) {
    warnings.push("Aucune information exploitable n’a été trouvée dans la réponse.");
  }

  return {
    parsed: true,
    values,
    warnings: uniqueWarnings(warnings),
  };
}
